import React from 'react';
import { motion } from 'motion/react';
import { Target, Shield, Heart, EyeOff, Key, BookOpen, Sparkles } from 'lucide-react';
import { Button } from '../components/ui';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

export function MissionPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white text-navy-900 dark:text-white dark:bg-[#050505]">
      <SEO title="Our Mission" description="Building a friendlier, simpler way for every community to stay organized together with OrgSets." />
      <PublicHeader />
      
      <main className="pb-24">
        <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[50vh] bg-black text-white dark:bg-white dark:text-black mb-24">
          <div className="p-8 pt-28 md:p-16 md:pt-32 lg:p-12 lg:pt-32 xl:p-24 xl:pt-40 flex flex-col justify-center h-full border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white bg-white dark:bg-black text-black dark:text-white">
            <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words">
              Our Mission <br/> & Values.
            </h1>
            <p className="text-xl md:text-2xl font-bold leading-snug mb-12">
              Reclaiming digital spaces for real-world communities.
            </p>
          </div>
          <div className="p-8 md:p-16 flex flex-col justify-center items-center h-full relative overflow-hidden bg-brand-primary text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500 rounded-bl-full border-b-[8px] border-l-[8px] border-black dark:border-white" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-secondary rounded-tr-full border-t-[8px] border-r-[8px] border-black dark:border-white" />
            <div className="text-5xl md:text-7xl font-black uppercase tracking-widest text-center relative z-10 leading-[0.9]">
              Human <br/> <span className="text-brand-secondary">First</span>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-6">

          {/* Pillars List */}
          <div className="space-y-32 mb-32">
            
            {/* Pillar 1 */}
            <section className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-brand-primary" />
                </div>
                <h2 className="text-3xl font-black font-display mb-4 text-navy-900 dark:text-white">Room to Grow</h2>
                <p className="opacity-70 leading-relaxed text-base mb-4">
                  We believe that great ideas and strong groups shouldn't be held back by chaotic, cluttered communications. Our mission is to provide every community—from a cozy, local book club to a growing regional team or national association—with a professional, structured home.
                </p>
                <p className="opacity-70 leading-relaxed text-base">
                  By providing tools that automatically scale with your group's size and complexity, we ensure your organization is set up for success both today and years down the line.
                </p>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-xl aspect-square bg-gray-150 dark:bg-slate-800">
                <img 
                  src="/images/app_dashboard_screenshot.png" 
                  alt="Product UI Screenshot"
                  className="w-full h-full object-cover block"
                  referrerPolicy="no-referrer"
                />
              </div>
            </section>

            {/* Pillar 2 */}
            <section className="grid md:grid-cols-2 gap-16 items-center">
              <div className="rounded-3xl overflow-hidden shadow-xl aspect-square bg-gray-150 dark:bg-slate-800 md:order-2">
                <img 
                  src="/images/app_dashboard_screenshot.png" 
                  alt="Product UI Screenshot"
                  className="w-full h-full object-cover block"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="md:order-1">
                <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <Shield className="w-6 h-6 text-brand-primary" />
                </div>
                <h2 className="text-3xl font-black font-display mb-4 text-navy-900 dark:text-white">Built on Trust</h2>
                <p className="opacity-70 leading-relaxed text-base mb-4">
                  Trust is the foundation of any community. We build tools that make transparency effortless. From public financial ledgers that build integrity, to secure document vaults, to clear governance tools like democratic polls and structured leadership roles.
                </p>
                <p className="opacity-70 leading-relaxed text-base">
                  When members feel informed, respected, and connected to the group's operations, the entire community thrives.
                </p>
              </div>
            </section>

            {/* Pillar 3 */}
            <section className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-6">
                  <Sparkles className="w-6 h-6 text-brand-primary" />
                </div>
                <h2 className="text-3xl font-black font-display mb-4 text-navy-900 dark:text-white">Human Connection First</h2>
                <p className="opacity-70 leading-relaxed text-base mb-4">
                  Digital spaces should serve the physical world, not the other way around. OrgSets is designed with a single goal: to minimize administrative work so you can maximize face-to-face interaction.
                </p>
                <p className="opacity-70 leading-relaxed text-base">
                  We handle the event reminders, calendar coordination, parent sign-ups, and form vaults in the background. That way, scout troops can focus on the trail, sports teams can focus on the field, and community groups can focus on making a difference.
                </p>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-xl aspect-square bg-gray-150 dark:bg-slate-800">
                <img 
                  src="/images/app_dashboard_screenshot.png" 
                  alt="Product UI Screenshot"
                  className="w-full h-full object-cover block"
                  referrerPolicy="no-referrer"
                />
              </div>
            </section>
          </div>

          {/* Commitments Section */}
          <section className="mb-32">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold tracking-tight mb-4">Our Commitments</h2>
              <p className="text-lg opacity-60 max-w-2xl mx-auto">
                How we protect and support the integrity of your organization.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-8 rounded-3xl border bg-gray-50 border-gray-100 dark:bg-white/5 dark:border-white/10">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-6 text-brand-primary">
                  <EyeOff size={20} />
                </div>
                <h3 className="text-lg font-bold mb-3">No Attention Harvesting</h3>
                <p className="opacity-75 text-sm leading-relaxed">
                  We don't show ads, track member activity for marketing, or use algorithms designed to keep you online. We keep it quiet and focused.
                </p>
              </div>

              <div className="p-8 rounded-3xl border bg-gray-50 border-gray-100 dark:bg-white/5 dark:border-white/10">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-6 text-brand-primary">
                  <Key size={20} />
                </div>
                <h3 className="text-lg font-bold mb-3">Safety & Governance</h3>
                <p className="opacity-75 text-sm leading-relaxed">
                  Advanced role authorization levels and child-safety-conscious design. Parents are linked to youth accounts with full monitoring access.
                </p>
              </div>

              <div className="p-8 rounded-3xl border bg-gray-50 border-gray-100 dark:bg-white/5 dark:border-white/10">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center mb-6 text-brand-primary">
                  <BookOpen size={20} />
                </div>
                <h3 className="text-lg font-bold mb-3">Data Portability</h3>
                <p className="opacity-75 text-sm leading-relaxed">
                  Your data belongs to your community. Export your directories, roster histories, ledgers, and calendar events at any time.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="p-12 rounded-[2rem] text-center text-white relative overflow-hidden shadow-2xl bg-navy-900 dark:bg-slate-900 dark:border dark:border-white/10"
          >
            <Heart className="w-12 h-12 text-brand-primary mx-auto mb-6" />
            <h2 className="text-4xl font-black font-display mb-6 tracking-tight text-white animate-pulse-subtle">Build a better way to connect</h2>
            <p className="text-white/60 mb-10 max-w-lg mx-auto text-lg">
              We're just getting started. Join our growing group of organized communities and start your Organization today.
            </p>
            <Button size="lg" className="bg-brand-primary hover:bg-brand-primary/90 rounded-xl font-bold px-12 h-16 border-none relative z-10" onClick={() => navigate('/sign-up')}>
              Start Your Organization
            </Button>
            <div className="absolute -left-20 -top-20 w-80 h-80 bg-brand-primary/10 rounded-full blur-[100px]" />
          </motion.div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
